import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "../../../../../components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import DOMPurify from "dompurify";

const FIELD_CONFIG = {
  firstname: {
    name: "firstname",
    label: "Vorname",
    placeholder: "Jakub",
    type: "text",
  },
  lastname: {
    name: "lastname",
    label: "Nachname",
    placeholder: "Novák",
    type: "text",
  },
  street: {
    name: "street",
    label: "Straße",
    placeholder: "Goldene Gasse",
    type: "text",
  },
  postalCode: {
    name: "postalCode",
    label: "Postleitzahl",
    placeholder: "XXX XX",
    type: "text",
  },
  city: {
    name: "city",
    label: "Stadt",
    placeholder: "Goldene Gasse",
    type: "text",
  },
  email: {
    name: "email",
    label: "E-mail",
    placeholder: "example@example.com",
    type: "email",
  },
  telephone: {
    name: "telephone",
    label: "Telefon",
    placeholder: "+49 XXX XXX XXX",
    type: "tel",
  },
  firstname2: {
    name: "firstname2",
    label: "Vorname",
    placeholder: "Jakub",
    type: "text",
  },
  lastname2: {
    name: "lastname2",
    label: "Nachname",
    placeholder: "Novák",
    type: "text",
  },
  email2: {
    name: "email2",
    label: "E-mail",
    placeholder: "example@example.com",
    type: "email",
  },
  telephone2: {
    name: "telephone2",
    label: "Telefon",
    placeholder: "+49 XXX XXX XXX",
    type: "tel",
  },
};

export default function UserDataForm({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;

  const isUltraInsider =
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const currentContent = gameQuestionsQuery?.content?.[currentQuestion];
  const requiredInputs = currentContent?.inputs || [];
  const hasExtraPersonalData = currentContent?.extraPersonalData;

  const mainFields = requiredInputs.filter((field) => !field.endsWith("2"));
  const extraFields = requiredInputs.filter((field) => field.endsWith("2"));

  const [formData, setFormData] = useState({});
  const [hasAccompanyingPerson, setHasAccompanyingPerson] = useState(false);

  if (!gameQuestionsQuery?.content) {
    return null;
  }

  useEffect(() => {
    const initialValues = requiredInputs.reduce((acc, fieldName) => {
      acc[fieldName] = "";
      return acc;
    }, {});

    setFormData(initialValues);
    setHasAccompanyingPerson(false);
  }, [currentQuestion, ident, requiredInputs]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAccompanyingToggle = () => {
    setHasAccompanyingPerson((prev) => {
      const next = !prev;

      if (!next) {
        setFormData((prevFormData) => {
          const clearedExtraFields = { ...prevFormData };

          extraFields.forEach((field) => {
            clearedExtraFields[field] = "";
          });

          return clearedExtraFields;
        });
      }

      return next;
    });
  };

  const isFormValid = useMemo(() => {
    if (!mainFields.length) return false;

    const mainValid = mainFields.every((fieldName) =>
      String(formData[fieldName] || "").trim(),
    );

    if (!mainValid) return false;

    if (hasExtraPersonalData && hasAccompanyingPerson) {
      return extraFields.every((fieldName) =>
        String(formData[fieldName] || "").trim(),
      );
    }

    return true;
  }, [
    formData,
    mainFields,
    extraFields,
    hasExtraPersonalData,
    hasAccompanyingPerson,
  ]);

  const handleButtonClick = () => {
    if (!isFormValid) return;

    const answerData = { ...formData };

    if (!hasAccompanyingPerson) {
      extraFields.forEach((field) => {
        delete answerData[field];
      });
    }

    setCurrentQuestion(currentQuestion + 1);

    setAnswers((prev) => [
      ...prev,
      {
        question_ident: currentContent.ident,
        answer_ident: {
          ...answerData,
          hasAccompanyingPerson,
        },
      },
    ]);
  };

  const renderFields = (fields) =>
    fields.map((fieldName) => {
      const field = FIELD_CONFIG[fieldName];

      if (!field) return null;

      return (
        <div key={field.name} className={`form-field ${field.name}`}>
          <label htmlFor={field.name}>{field.label}</label>
          <input
            id={field.name}
            name={field.name}
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.name] || ""}
            onChange={handleChange}
          />
        </div>
      );
    });

  return (
    <section
      className={`game-page user-data-form-mechanics ${ident} ${
        isUltraInsider ? "ultra-insider" : ""
      }`}
    >
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          />
        </PagesHeader>

        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />

            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(currentContent.question),
                }}
              />
            </div>

            <div className="form-wrapper">{renderFields(mainFields)}</div>

            {hasExtraPersonalData && (
              <>
                <label className="accompanying-toggle">
                  <input
                    type="checkbox"
                    checked={hasAccompanyingPerson}
                    onChange={handleAccompanyingToggle}
                  />
                  <span>Begleitperson hinzufügen</span>
                </label>

                {hasAccompanyingPerson && (
                  <>
                    {currentContent?.extraPersonalDataCopy && (
                      <div className="extra-personal-data-copy">
                        <h2
                          className="question"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(currentContent.extraPersonalDataCopy),
                          }}
                        />
                      </div>
                    )}

                    <div className="form-wrapper extra-form-wrapper">
                      {renderFields(extraFields)}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            {gameQuestionsQuery.content[currentQuestion].desktopImgHid ? (
              selected ===
              gameQuestionsQuery.content[currentQuestion].correctAnswer ? (
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImg" : "desktopImg"
                    ]
                  }
                  alt=""
                />
              ) : (
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImgHid" : "desktopImgHid"
                    ]
                  }
                  alt=""
                />
              )
            ) : (
              <div className="img-wrapper">
                <img
                  src={
                    gameQuestionsQuery.content[currentQuestion][
                      width < 1020 ? "mobileImg" : "desktopImg"
                    ]
                  }
                  alt=""
                />
                {gameQuestionsQuery.content[currentQuestion]?.sideImageCopy && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html:
                        DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion]
                          ?.sideImageCopy),
                    }}
                  />
                )}
              </div>
            )}

            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button action={handleButtonClick} disabled={!isFormValid}>
                    {currentContent.nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton action={handleButtonClick} disabled={!isFormValid}>
          {currentContent.nextButtonCopy ?? "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
